import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { FormDataService } from '../../services/form-data.service';
import { GuidService } from '../../services/guid.service';
import { DynamicFormComponent } from '../dynamic-form/dynamic-form/dynamic-form.component';
import { ShowlistComponent } from '../list/showlist/showlist.component';

// 表單資料記錄介面
export interface FormDataRecord {
  id: string;
  formName: string;
  data: any;
  createdAt: Date;
  updatedAt: Date;
}

// 表單配置介面
export interface FormConfig {
  formid:string;
  formName?: string;
  formTitle: string;
  maxRecords?: number;
  required?: boolean;
}

@Component({
  selector: 'app-form-data-card',
  standalone: true,
  imports: [CommonModule, DynamicFormComponent, ShowlistComponent],
  templateUrl: './form-data-card.component.html',
  styleUrl: './form-data-card.component.scss'
})
export class FormDataCardComponent implements OnInit, OnDestroy {
  @Input() formid: string = '';
  @Input() formName: string = '';
  @Input() formTitle: string = '';
  @Input() maxRecords?: number;
  @Input() required: boolean = false;

  @Output() addData = new EventEmitter<string>();
  @Output() viewList = new EventEmitter<string>();
  @Output() viewDetail = new EventEmitter<{formName: string, recordId: string}>();

  // 表單資料統計
  recordCount: number = 0;
  records: FormDataRecord[] = [];
  private subscription?: Subscription;

  // 彈出視窗狀態管理
  showFormModal: boolean = false;
  showListModal: boolean = false;
  isLoading: boolean = false;
  errorMessage: string = '';

  constructor(
    private formDataService: FormDataService,
    private guidService: GuidService
  ) {}

  ngOnInit(): void {
    this.loadFormData();
  }

  // 載入表單資料
  private loadFormData(): void {
    this.subscription = this.formDataService.getFormRecords(this.formid).subscribe({
      next: (records) => {
        this.records = records;
        this.recordCount = this.records.length;
      },
      error: (error) => {
        console.error('載入表單資料失敗:', error);
        this.records = [];
        this.recordCount = 0;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  // 新增資料
  onAddData(): void {
    // 檢查是否達到最大筆數限制
    if (this.maxRecords && this.recordCount >= this.maxRecords) {
      return;
    }

    // 開啟表單填寫彈出視窗
    this.showFormModal = true;
    this.errorMessage = '';
  }

  // 查看列表
  onViewList(): void {
    // 開啟列表顯示彈出視窗
    this.showListModal = true;
    this.errorMessage = '';
  }

  // 關閉表單彈出視窗
  closeFormModal(): void {
    this.showFormModal = false;
    this.errorMessage = '';
  }

  // 關閉列表彈出視窗
  closeListModal(): void {
    this.showListModal = false;
    this.errorMessage = '';
  }

  // 處理表單提交成功
  onFormSubmitSuccess(): void {
    this.closeFormModal();
    // 重新載入資料以更新記錄數
    this.loadFormData();
  }

  // 處理列表資料變更（如編輯、刪除）
  onListDataChanged(): void {
    // 重新載入資料以更新記錄數
    this.loadFormData();
  }

  // 鍵盤事件處理 - ESC 關閉彈出視窗
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.showFormModal) {
      this.closeFormModal();
      event.preventDefault();
    } else if (this.showListModal) {
      this.closeListModal();
      event.preventDefault();
    }
  }

  // 查看詳細資料
  onViewDetail(recordId: string): void {
    this.viewDetail.emit({
      formName: this.formid,
      recordId: recordId
    });
  }

  // 檢查是否達到最大筆數限制
  get isMaxRecordsReached(): boolean {
    return this.maxRecords ? this.recordCount >= this.maxRecords : false;
  }

  // 取得狀態文字
  get statusText(): string {
    if (this.recordCount === 0) {
      return '未填寫';
    }

    if (this.maxRecords) {
      return `已填寫 ${this.recordCount} / ${this.maxRecords} 筆`;
    }

    return `已填寫 ${this.recordCount} 筆`;
  }

  // 取得狀態類別
  get statusClass(): string {
    if (this.recordCount === 0) {
      return this.required ? 'status-required-empty' : 'status-empty';
    }

    if (this.maxRecords && this.recordCount >= this.maxRecords) {
      return 'status-completed';
    }

    return 'status-filled';
  }
}
